import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, getCurrentBrand, BRAND_DISPLAY_NAME } from '../config/api';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Avatar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Fade,
  Slide,
  Grow
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';

const Login = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [animateIn, setAnimateIn] = useState(false);


  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setAnimateIn(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      setError(t('login.usernameRequired'));
      return false;
    }
    if (!formData.password.trim()) {
      setError(t('login.passwordRequired'));
      return false;
    }
    if (formData.username.length < 3) {
      setError(t('login.usernameMinLength'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          brand: getCurrentBrand() // Send brand name from frontend config
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Store auth token and user data in localStorage
        localStorage.setItem('authToken', result.token || 'user-token');
        localStorage.setItem('userData', JSON.stringify({
          ...result.user,
          displayName: result.user.username // Use username as display name
        }));
        localStorage.setItem('username', formData.username);
        
        // Call success callback
        onLoginSuccess();
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('login.invalidCredentials'));
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(t('login.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 3 },
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(52, 152, 219, 0.2) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(52, 152, 219, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(52, 152, 219, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: 'none'
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in={animateIn} timeout={1000}>
          <Box>
            {/* Logo and Header */}
            <Grow in={animateIn} timeout={1200}>
              <Box display="flex" flexDirection="column" alignItems="center" mb={{ xs: 4, sm: 6 }}>
                <Box
                  sx={{
                    position: 'relative',
                    mb: 3
                  }}
                >
                  <Avatar
                    sx={{
                      width: { xs: 70, sm: 90, md: 100 },
                      height: { xs: 70, sm: 90, md: 100 },
                      bgcolor: '#3498db',
                      color: 'white',
                      boxShadow: '0 20px 60px rgba(52, 152, 219, 0.4), 0 8px 25px rgba(52, 152, 219, 0.2)',
                      border: '4px solid rgba(255,255,255,0.2)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -8,
                        left: -8,
                        right: -8,
                        bottom: -8,
                        background: 'linear-gradient(45deg, #3498db, #2980b9)',
                        borderRadius: '50%',
                        zIndex: -1,
                        opacity: 0.3,
                        filter: 'blur(8px)'
                      }
                    }}
                  >
                    <DashboardIcon sx={{ fontSize: { xs: 35, sm: 45, md: 50 } }} />
                  </Avatar>
                </Box>
                
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 800, 
                    color: 'white',
                    mb: 1,
                    textAlign: 'center',
                    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                    textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    letterSpacing: '-0.02em'
                  }}
                >
                  {BRAND_DISPLAY_NAME}
                </Typography>
                
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.9)',
                    fontWeight: 400,
                    textAlign: 'center',
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                    textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    maxWidth: '400px'
                  }}
                >
                  {t('login.analyticsDashboard')}
                </Typography>
              </Box>
            </Grow>

            {/* Login Form */}
            <Slide direction="up" in={animateIn} timeout={1400}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4, md: 5 },
                  borderRadius: { xs: 2, sm: 3, md: 4 },
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 25px 80px rgba(0,0,0,0.15), 0 10px 40px rgba(0,0,0,0.1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #3498db, #2980b9, #3498db)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 3s ease-in-out infinite'
                  }
                }}
              >
                <Typography 
                  variant="h4" 
                  sx={{ 
                    mb: { xs: 3, sm: 4 }, 
                    fontWeight: 700, 
                    color: '#2c3e50',
                    textAlign: 'center',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                  }}
                >
                  {t('login.welcomeBack')}
                </Typography>

                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 4, 
                    color: 'text.secondary',
                    textAlign: 'center',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.6
                  }}
                >
                  {t('login.subtitle')}
                </Typography>

                {error && (
                  <Grow in={!!error} timeout={300}>
                    <Alert 
                      severity="error" 
                      sx={{ 
                        mb: 3, 
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(231, 76, 60, 0.2)'
                      }}
                      onClose={() => setError('')}
                    >
                      {error}
                    </Alert>
                  </Grow>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label={t('login.username')}
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: '#3498db', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3498db',
                          borderWidth: 2
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3498db',
                          borderWidth: 2
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label={t('login.password')}
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SecurityIcon sx={{ color: '#3498db', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={isLoading}
                            sx={{
                              color: '#3498db',
                              '&:hover': {
                                backgroundColor: 'rgba(52, 152, 219, 0.1)'
                              }
                            }}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 4,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3498db',
                          borderWidth: 2
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3498db',
                          borderWidth: 2
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isLoading}
                    sx={{
                      py: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      fontWeight: 700,
                      borderRadius: 2,
                      background: 'linear-gradient(45deg, #3498db, #2980b9)',
                      boxShadow: '0 8px 25px rgba(52, 152, 219, 0.4)',
                      textTransform: 'none',
                      letterSpacing: '0.5px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #2980b9, #1f5f8b)',
                        boxShadow: '0 12px 35px rgba(52, 152, 219, 0.5)',
                        transform: 'translateY(-2px)'
                      },
                      '&:active': {
                        transform: 'translateY(0)'
                      }
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      t('login.signIn')
                    )}
                  </Button>
                </Box>

              </Paper>
            </Slide>
          </Box>
        </Fade>
      </Container>

      {/* CSS Animation for shimmer effect */}
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}
      </style>
    </Box>
  );
};

export default Login;
